import Button from "components/molecules/Button";
import ModalContainer from "components/molecules/ModalContainer";
import Table from "components/molecules/Table";
import TableLayout from "components/molecules/TableLayout";
import Toast from "components/molecules/Toast";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {CgEye} from "react-icons/cg";
import styled from "styled-components";
import analyticsService from "services/analyticsService";
import ItemDetailModal from "./ItemDetailModal";
import downIcon from "../../../../assets/Reports/downIcon.svg";
import upIcon from "../../../../assets/Reports/upIcon.svg";

// Cycle through pill colours deterministically by category name
const CATEGORY_CLASSES = ["blue", "red", "orange", "purple", "gray", "green"];
const getCategoryClass = name => {
    if (!name) return "gray";
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return CATEGORY_CLASSES[Math.abs(hash) % CATEGORY_CLASSES.length];
};

const StyledStoreSummary = styled.div`
    width: 100%;

    .category {
        border-radius: 30px;
        padding: 7px 10px;
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        &.blue {
            color: rgba(44, 185, 255, 1);
            background: rgba(44, 185, 255, 0.2);
        }
        &.red {
            color: rgba(226, 29, 37, 1);
            background: rgba(226, 29, 37, 0.2);
        }
        &.orange {
            color: rgba(245, 126, 42, 1);
            background: rgba(245, 126, 42, 0.2);
        }
        &.purple {
            color: rgba(94, 64, 153, 1);
            background: rgba(94, 64, 153, 0.2);
        }
        &.gray {
            color: rgba(119, 119, 119, 1);
            background: rgba(236, 236, 236, 1);
        }
        &.green {
            color: rgba(39, 196, 83, 1);
            background: rgba(39, 196, 83, 0.15);
        }
    }

    .action-btn {
        width: 26px;
        height: 26px;
        background-color: rgba(236, 236, 236, 1);
        border-radius: 5px;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: background 0.2s;
        &:hover {
            background-color: rgba(245, 126, 42, 0.15);
        }
    }

    .btn-holder {
        display: flex;
        flex-direction: column;
        gap: 10px;
        @media (min-width: 576px) {
            flex-direction: row;
            gap: 15px;
        }
    }

    .table-head {
        th:last-child {
            text-align: start;
        }
    }
`;

/**
 * StoreSummary
 *
 * Props (from parent reports.jsx):
 *   restaurantId, storeId, startDate, endDate
 */
const StoreSummary = ({restaurantId, storeId, startDate, endDate}) => {
    const [sortBy, setSortBy] = useState("quantity");
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        itemsPerPage: 7,
        totalCount: 0,
    });

    const [topCustomersData, setTopCustomersData] = useState([]);
    const [topCustomersLoading, setTopCustomersLoading] = useState(false);
    const [customerPagination, setCustomerPagination] = useState({page: 1, itemsPerPage: 5});

    // ─── Fetch ────────────────────────────────────────────────────────────────
    const fetchSummary = useCallback(
        async ({page = 1, limit = 7, sort = sortBy} = {}) => {
            if (!restaurantId) return;
            setLoading(true);
            try {
                const res = await analyticsService.getStoreSummary({
                    restaurantId,
                    storeId: storeId || undefined,
                    sortBy: sort,
                    page,
                    limit,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                });
                setItems(res?.data?.items || []);
                setPagination(prev => ({
                    ...prev,
                    page,
                    itemsPerPage: limit,
                    totalCount: res?.data?.pagination?.totalItems || 0,
                }));
            } catch (err) {
                Toast({type: "error", message: err.message || "Failed to load store summary"});
            } finally {
                setLoading(false);
            }
        },
        [restaurantId, storeId, startDate, endDate, sortBy]
    );

    useEffect(() => {
        fetchSummary({page: 1, limit: pagination.itemsPerPage, sort: sortBy});
    }, [restaurantId, storeId, startDate, endDate, sortBy]);

    // ─── Fetch Top Customers ──────────────────────────────────────────────────
    useEffect(() => {
        if (!restaurantId) return;
        const fetchTopCustomers = async () => {
            setTopCustomersLoading(true);
            try {
                const res = await analyticsService.getWeeklyReport({
                    restaurantId,
                    storeId: storeId || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                });
                setTopCustomersData(res?.data?.topCustomers || []);
            } catch (err) {
                console.error("Failed to load top customers:", err);
            } finally {
                setTopCustomersLoading(false);
            }
        };
        fetchTopCustomers();
    }, [restaurantId, storeId, startDate, endDate]);

    // ─── Growth badge ─────────────────────────────────────────────────────────
    const GrowBadge = customer => {
        const pct = customer?.growthPct ?? 0;
        const isUp = customer?.growIncrement !== false && pct >= 0;
        return (
            <span
                style={{
                    color: isUp ? "rgba(39, 196, 83, 1)" : "rgba(224, 0, 0, 1)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                }}>
                <img src={isUp ? upIcon : downIcon} alt={isUp ? "up" : "down"} />
                {Math.abs(pct).toFixed(1)}%
            </span>
        );
    };

    // ─── Top Customers Table data ─────────────────────────────────────────────
    const topCustomerColumns = ["Customer", "Orders (Week)", "Revenue (Week)", "Growth", "All-Time Revenue"];
    const topCustomerRows = useMemo(() => {
        const startIdx = (customerPagination.page - 1) * customerPagination.itemsPerPage;
        return topCustomersData
            .slice(startIdx, startIdx + customerPagination.itemsPerPage)
            .map(c => [
                c?.customerName || "—",
                c?.weeklyOrders ?? "—",
                `$${Number(c?.weeklyRevenue || 0).toFixed(2)}`,
                GrowBadge(c),
                `$${Number(c?.allTimeRevenue || 0).toFixed(2)}`,
            ]);
    }, [topCustomersData, customerPagination]);

    // ─── Sort handlers ────────────────────────────────────────────────────────
    const handleSortQuantity = () => setSortBy("quantity");
    const handleSortRevenue = () => setSortBy("revenue");

    // ─── Cell renderers ───────────────────────────────────────────────────────
    const CategoryPill = name => <span className={`category ${getCategoryClass(name)}`}>{name || "—"}</span>;

    const ActionBtns = data => (
        <ModalContainer
            width="466"
            title="Item Details"
            content={() => <ItemDetailModal data={data} />}
            btnComponent={({onClick}) => (
                <div className="action-btn" onClick={onClick}>
                    <CgEye color="rgba(49, 49, 49, 1)" />
                </div>
            )}
        />
    );

    const RevenueCell = val => <span style={{color: "rgba(39, 196, 83, 1)"}}>${Number(val || 0).toFixed(2)}</span>;

    // ─── Table rows ───────────────────────────────────────────────────────────
    const {rowsData, totalCount} = useMemo(() => {
        return {
            rowsData: items.map(item => [
                CategoryPill(item?.categoryName),
                item?.itemName || "—",
                `${item?.totalQuantity ?? 0} pcs`,
                `$${Number(item?.unitPrice || 0).toFixed(2)}`,
                RevenueCell(item?.totalRevenue),
                ActionBtns(item),
            ]),
            totalCount: pagination.totalCount,
        };
    }, [items, pagination.totalCount]);

    const columnNames = ["Category", "Item Name", "Quantity Sold", "Unit Price", "Total Revenue", "Actions"];

    return (
        <StyledStoreSummary>
            <div style={{marginBottom: 30}}>
                <TableLayout
                    tableHeading="Top Customers This Week"
                    currentPage={customerPagination.page}
                    pageSize={customerPagination.itemsPerPage}
                    totalCount={topCustomersData.length}
                    onChangeFilters={f => setCustomerPagination(prev => ({...prev, ...f}))}>
                    <Table
                        rowsData={topCustomerRows}
                        columnNames={topCustomerColumns}
                        noPadding
                        loading={topCustomersLoading}
                        emptyMessage="No customer data for this period."
                    />
                </TableLayout>
            </div>

            <TableLayout
                tableHeading="Item & Category Breakdown"
                currentPage={pagination.page}
                pageSize={pagination.itemsPerPage}
                totalCount={totalCount}
                onChangeFilters={({page, itemsPerPage}) => {
                    setPagination(prev => ({...prev, page, itemsPerPage}));
                    fetchSummary({page, limit: itemsPerPage, sort: sortBy});
                }}
                content={
                    <div className="btn-holder">
                        <Button
                            width="160px"
                            onClick={handleSortQuantity}
                            style={sortBy === "quantity" ? {opacity: 1} : {opacity: 0.6}}>
                            Sort by Quantity
                        </Button>
                        <Button
                            width="160px"
                            gray
                            onClick={handleSortRevenue}
                            style={sortBy === "revenue" ? {opacity: 1} : {opacity: 0.6}}>
                            Sort by Revenue
                        </Button>
                    </div>
                }>
                <Table
                    rowsData={rowsData}
                    columnNames={columnNames}
                    noPadding
                    loading={loading}
                    emptyMessage="No item data for the selected period."
                />
            </TableLayout>
        </StyledStoreSummary>
    );
};

export default StoreSummary;
